import React from 'react';

interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  className?: string;
}

// Base skeleton component
export const Skeleton: React.FC<SkeletonProps> = ({ 
  width = '100%', 
  height = '20px', 
  borderRadius = '4px',
  className = ''
}) => {
  return (
    <div 
      className={`skeleton ${className}`}
      style={{ 
        width, 
        height, 
        borderRadius,
        backgroundColor: 'var(--color-stroke-weak)',
        animation: 'skeleton-pulse 2s ease-in-out infinite',
        display: 'block'
      }}
    />
  );
};

// Calendar-specific skeleton components
export const PropertySkeleton: React.FC = () => {
  return (
    <div className="property-cell property-cell-even" style={{ padding: '16px 12px' }}>
      <Skeleton width="80%" height="16px" />
    </div>
  );
};

export const HeaderSkeleton: React.FC = () => {
  return (
    <div className="grid-header grid-header-date">
      <Skeleton width="60px" height="14px" />
    </div>
  );
};

export const TaskSkeleton: React.FC = () => {
  return (
    <div className="task-badge" style={{ margin: '4px', padding: '8px' }}>
      <div className="task-content">
        <div className="task-header">
          <div className="task-tag">
            <Skeleton width="12px" height="12px" borderRadius="2px" />
            <Skeleton width="50px" height="11px" className="skeleton-margin-left" />
          </div>
          <div className="task-tag">
            <Skeleton width="12px" height="12px" borderRadius="2px" />
            <Skeleton width="30px" height="11px" className="skeleton-margin-left" />
          </div>
        </div>
        <Skeleton width="70%" height="12px" className="skeleton-margin-top" />
      </div>
    </div>
  );
};

export const ReservationSkeleton: React.FC<{ type: 'full' | 'half' }> = ({ type }) => {
  return (
    <div className="reservation-container reservation-container-center">
      <Skeleton 
        width={type === 'full' ? '100%' : 'calc(50% - 1px)'} 
        height="24px" 
        borderRadius="6px"
      />
      {type === 'half' && (
        <Skeleton 
          width="calc(50% - 1px)" 
          height="24px" 
          borderRadius="6px"
        />
      )}
    </div>
  );
};

export const DateCellSkeleton: React.FC = () => {
  const hasReservation = Math.random() > 0.7;
  const taskCount = Math.floor(Math.random() * 3);

  return (
    <div className="date-cell date-cell-even">
      <div className="date-cell-content">
        <div>
          {hasReservation && <ReservationSkeleton type="full" />}
          {Array.from({ length: taskCount }, (_, i) => (
            <TaskSkeleton key={i} />
          ))}
        </div>
        <Skeleton 
          width="60px" 
          height="24px" 
          borderRadius="6px"
          className="skeleton-add-button"
        />
      </div>
    </div>
  );
};

// Main calendar skeleton
export const CalendarSkeleton: React.FC<{ 
  propertyCount?: number;
  dateCount?: number;
}> = ({ 
  propertyCount = 5, 
  dateCount = 7 
}) => {
  return (
    <div className="calendar-wrapper">
      <div className="calendar-container">
        <div className="calendar-grid">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: `200px repeat(${dateCount}, 200px)`,
            minHeight: '500px'
          }}>
            {/* Header row */}
            <div className="grid-header grid-header-property">
              <Skeleton width="80px" height="16px" />
            </div>
            {Array.from({ length: dateCount }, (_, i) => (
              <HeaderSkeleton key={`header-${i}`} />
            ))}
            
            {/* Property rows */}
            {Array.from({ length: propertyCount }, (_, rowIndex) => (
              <React.Fragment key={`row-${rowIndex}`}>
                <PropertySkeleton />
                {Array.from({ length: dateCount }, (_, colIndex) => (
                  <DateCellSkeleton key={`cell-${rowIndex}-${colIndex}`} />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact skeleton for smaller loading states
export const CompactCalendarSkeleton: React.FC = () => {
  return (
    <div className="calendar-container" style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <Skeleton width="200px" height="24px" borderRadius="8px" />
        <Skeleton width="300px" height="14px" className="skeleton-margin-top" />
      </div>
      <div className="calendar-grid" style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Skeleton width="60px" height="60px" borderRadius="50%" />
          <Skeleton width="120px" height="16px" className="skeleton-margin-center-lg" />
          <Skeleton width="200px" height="12px" className="skeleton-margin-center" />
        </div>
      </div>
    </div>
  );
};